// ============================================================================
// THE EVIDENCE DRAWER — prove any figure on the board.
// ----------------------------------------------------------------------------
// Click a mark and this opens with the exact rows behind it. Not "a filtered
// list that should match": literally `cell.select(rows)`, the same function that
// produced the number. The count in this head and the figure on the panel come
// from one source, so they cannot drift apart.
//
// Three things stack above the rows, in the order a sceptical reader wants them:
//
//   THE WORKING LINE  the arithmetic in words, over the rows actually selected,
//                     recomputed here rather than echoed from the cell, so a
//                     proof that cannot reproduce its own figure shows it
//   THE FIELDS        which API fields the panel read, as chips
//   THE SOURCE        the panel's stamp, verbatim
//
// Slice 4 (Act): a waiting row carries Won and Lost chips, so the answer gets
// recorded at the exact moment the operator is looking at the evidence. That is
// the loop the Outcomes panel names in its activation note: this drawer IS the
// tool that closes the thin-data gap. The write goes through
// DataService.projects.recordOutcome (PATCH /:id/outcome — the guarded route
// that stamps ai_context.wonAt/lostAt and refuses to clobber corpus dates), the
// row swaps its chips for a mono "Recorded" and DOES NOT leave the list, so
// nothing shifts under the cursor. Other lanes carry their open affordance in
// the same slot (Resume / Open quote / Open) so the act column always answers
// "what can I do with this row".
// ============================================================================

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Icons } from '../ui/Icon';
import { useModalAccessibility } from '../../utils/use-modal-accessibility';
import ConfirmModal from '../modals/ConfirmModal';
import { DataService } from '../../services/data-service';
import { broadcastProjectsChanged } from '../../services/project-changes-channel';
import type { Cell, PanelDef, AnalysisContext } from '../../services/analysis/registry';
import { money, PERIOD_LABELS, type DerivedRow } from '../../services/analysis/model';
import { matchesRunSheetSearch } from '../../services/history-search';
import { buildCsv, downloadCsv, yyyymmdd } from '../../utils/csv';

interface Props {
    def: PanelDef;
    /** Null when the whole panel is being proved from its stamp. */
    cell: Cell | null;
    ctx: AnalysisContext;
    onClose: () => void;
    /** Cross-page handoff into the Run Sheet. */
    onNavigate?: (page: string, options?: any) => void;
    /** Opens the job itself. Same handlers the Run Sheet uses. */
    onLoadProject?: (project: any) => void;
    onOpenQuote?: (project: any) => void;
    /**
     * Same-tab state echo after a successful outcome write. The durable write
     * and the cross-tab broadcast both happen HERE; but broadcastProjectsChanged
     * deliberately ignores its own tab (App.jsx patches its state synchronously
     * at every other mutation site), so without this callback THIS tab's board
     * would keep contradicting the decision just recorded until a reload.
     * Optional: absent, the drawer still records and broadcasts — a no-op
     * halfway state per the cross-session discipline.
     */
    onOutcomeRecorded?: (projectId: string, status: 'won' | 'lost', persisted: any) => void;
}

/** Age pill bands, the Run Sheet's values verbatim so the two read the same. */
function ageTone(days: number): 'ok' | 'warm' | 'cold' {
    if (days <= 21) return 'ok';
    if (days <= 60) return 'warm';
    return 'cold';
}

/**
 * Lane labels. The registry's lane KEYS are internal identifiers, and shipping
 * "finish" or "send" to a builder is jargon leaking through a seam. These are the
 * Run Sheet's own labels, so a row reads the same on both surfaces.
 */
const LANE_LABEL: Record<string, string> = {
    finish: 'Needs finishing',
    send: 'Ready to send',
    waiting: 'Awaiting answer',
    decided: 'Decided',
};

/** What one row's act cell is doing. Absent from the map = chips at rest. */
type ActState = 'saving' | 'recorded' | 'failed';

export default function EvidenceDrawer({ def, cell, ctx, onClose, onNavigate, onLoadProject, onOpenQuote, onOutcomeRecorded }: Props) {
    const ref = useRef<HTMLDivElement | null>(null);

    // Per-row act state. Keyed by project id, deliberately NOT synced to the
    // rows memo: a recorded row must stay in the list exactly as it was, with
    // "Recorded" where its chips were, so nothing shifts under the cursor. The
    // board behind updates on the next projects refresh, not mid-drawer.
    const [acts, setActs] = useState<Record<string, ActState>>({});
    // The row awaiting the Mark-lost confirmation. Lost is destructive-adjacent
    // (it counts against the win rate), so it gets a named-choice ConfirmModal;
    // Won writes immediately.
    const [pendingLost, setPendingLost] = useState<DerivedRow | null>(null);
    // Screen-reader announcement for the last act result.
    const [actNotice, setActNotice] = useState('');
    // Quote-corpus nomination state per row (migration 089). 'queued' means
    // the quote is waiting in the DevTools Quote Corpus review queue — a
    // nomination never enters retrieval without operator approval there.
    const [corpusActs, setCorpusActs] = useState<Record<string, 'saving' | 'queued' | 'failed'>>({});

    const nominateRow = useCallback(async (r: DerivedRow) => {
        setCorpusActs(prev => ({ ...prev, [r.id]: 'saving' }));
        try {
            const full = await DataService.projects.getById(r.id);
            const { nominateProject } = await import('../../services/quote-corpus-nomination.js');
            const res: any = await nominateProject(full);
            setCorpusActs(prev => ({ ...prev, [r.id]: 'queued' }));
            setActNotice(res?.alreadyNominated
                ? `${r.quoteNumber || 'Quote'} is already in the corpus review queue.`
                : `${r.quoteNumber || 'Quote'} nominated to the corpus review queue.`);
        } catch (err: any) {
            console.warn('[QM] Corpus nomination failed:', err?.message);
            setCorpusActs(prev => ({ ...prev, [r.id]: 'failed' }));
            setActNotice(err?.message
                ? `${r.quoteNumber || 'Quote'} was not nominated: ${err.message}`
                : `${r.quoteNumber || 'Quote'} could not be nominated. Nothing changed.`);
        }
    }, []);

    const recordRow = useCallback(async (r: DerivedRow, status: 'won' | 'lost') => {
        setActs(prev => ({ ...prev, [r.id]: 'saving' }));
        try {
            const persisted = await DataService.projects.recordOutcome(r.id, { status });
            setActs(prev => ({ ...prev, [r.id]: 'recorded' }));
            setActNotice(`${r.quoteNumber || 'Quote'} recorded as ${status}.`);
            // Other tabs re-fetch on this; THIS tab is patched via the optional
            // onOutcomeRecorded echo (the channel ignores its own tab).
            broadcastProjectsChanged({ kind: 'status', projectIds: [r.id] });
            if (onOutcomeRecorded) onOutcomeRecorded(r.id, status, persisted);
        } catch (err: any) {
            // The chips stay live for a retry; the row says "Not saved" where its
            // lane read, because the status column is exactly where "the status
            // write did not land" belongs.
            console.warn('[QM] Analysis outcome write failed:', err?.message);
            setActs(prev => ({ ...prev, [r.id]: 'failed' }));
            setActNotice(`${r.quoteNumber || 'Quote'} not saved. Nothing changed.`);
        }
    }, [onOutcomeRecorded]);

    /**
     * Escape must only close the drawer when the drawer is the topmost thing.
     *
     * Opening a job raises an unsaved-changes confirmation ON TOP of this
     * drawer. Both it and this listen for Escape on `document`, and
     * stopPropagation does not stop a sibling listener on the same node, so one
     * Escape used to dismiss the confirm AND the drawer together, dropping the
     * operator back on the board having lost their place. That is precisely the
     * outcome cancelling was supposed to avoid.
     */
    const closeIfTopmost = useCallback(() => {
        if (typeof document !== 'undefined') {
            const others = Array.from(document.querySelectorAll('[role="dialog"]'))
                .filter((d) => d !== ref.current && !ref.current?.contains(d));
            if (others.length > 0) return;
        }
        onClose();
    }, [onClose]);

    const modalProps = useModalAccessibility(ref, closeIfTopmost, true, 'an-ev-title');

    // The panel's own source set: respectsPeriod:false panels prove against the
    // unfiltered book, exactly as they computed against it. Proving a figure over
    // a different set from the one that produced it is the whole failure mode
    // this drawer exists to prevent.
    const source: DerivedRow[] = def.respectsPeriod ? ctx.rows : ctx.allRows;

    const rows = useMemo(() => {
        // No cell means the STAMP door. Prove the population the stamp actually
        // COUNTS, not the whole set the panel was handed: three panels count
        // narrower than their source, and without this the drawer contradicted
        // the very stamp it renders four rows above, erring larger.
        const base = cell
            ? cell.select(source)
            : (def.population ? def.population(source) : source);
        return base.slice().sort((a, b) => b.value - a.value);
    }, [cell, def, source]);

    const series = useMemo(
        () => def.compute(def.respectsPeriod ? ctx : { ...ctx, rows: ctx.allRows }),
        [def, ctx],
    );

    const total = rows.reduce((s, r) => s + r.value, 0);
    const working = cell ? cell.formula(rows) : `${rows.length} rows  =  ${money(total)} ex GST`;
    const title = cell ? `${def.title}: ${cell.label}` : def.title;

    // THE SCOPE. A proof whose denominator is off screen is not a proof. This
    // drawer covers the board, so the period buttons and the filter box that
    // produced these rows are both invisible while it is open: filter to
    // "Waratah", open Concrete repair, and six jobs reads as "we only do six
    // concrete repairs" with nothing on screen to say otherwise. A screenshot of
    // that is unfalsifiable.
    const trimmedQuery = (ctx.query || '').trim();
    const scopeLine = def.respectsPeriod
        ? `${PERIOD_LABELS[ctx.period]}${trimmedQuery ? ` · filtered by "${trimmedQuery}"` : ''}`
        : 'All time · this panel ignores the period and the filter';

    // The reconcile line. If the proof cannot reproduce the figure it was opened
    // from, say so IN THE DRAWER rather than letting the operator discover the
    // discrepancy by counting. Silence here would make the whole surface a liar.
    //
    // Deliberately NOT gated on `cell`: the stamp door needs this check most,
    // because that is the case that was silently wrong. series.total is the
    // panel's population, which is exactly what the stamp census reports.
    const expected = cell ? { count: cell.count, value: cell.value } : series.total;
    const mismatch = rows.length !== expected.count || Math.round(total) !== Math.round(expected.value);

    /**
     * THE DOOR, and an honest label on it.
     *
     * `drill.lane === null` means the panel has no truthful Run Sheet handoff for
     * this mark, and no button is offered. Sending the operator to a lane that
     * does not contain these rows would be worse than not offering the trip.
     *
     * The reconcile line states the difference between what they are looking at
     * and what they will land on, computed rather than assumed. The Run Sheet
     * carries no period, and its search is a substring match, so a drill is often
     * a SUPERSET. Saying "52 jobs" and then landing on 61 without a word is how a
     * surface built to be trusted stops being trusted.
     */
    const handoff = useMemo(() => {
        const d = cell?.drill;
        if (!d || d.lane === null) return null;
        const laneLabel = d.lane === 'all' ? 'the whole book' : (LANE_LABEL[d.lane] || d.lane).toLowerCase();
        return {
            label: d.query
                ? `Open "${d.query}" in the run sheet`
                : `Open ${laneLabel} in the run sheet`,
            options: { lane: d.lane, query: d.query, sort: d.sort },
        };
    }, [cell]);

    const handoffNote = useMemo(() => {
        const d = cell?.drill;
        if (!d || d.lane === null) return null;
        // Count what the destination will actually hold, using the same matcher
        // the Run Sheet uses, over the same unfiltered book it shows.
        const landing = ctx.allRows.filter((r) => {
            if (d.lane !== 'all' && r.lane !== d.lane) return false;
            if (d.query && !matchesRunSheetSearch(r.raw, d.query)) return false;
            return true;
        });
        if (landing.length === rows.length) return d.note || null;
        const more = landing.length - rows.length;
        const why = more > 0
            ? `The run sheet has no period filter, so it will show ${landing.length}, ${more} more than the ${rows.length} here.`
            : `The run sheet will show ${landing.length}, fewer than the ${rows.length} here.`;
        return d.note ? `${why} ${d.note}` : why;
    }, [cell, ctx.allRows, rows.length]);

    /**
     * S6: hand these exact rows to a spreadsheet, immediately, no dialog. The
     * columns are the columns on screen — an export that reorders or renames
     * what the operator is looking at invites "which file is this from" doubt.
     * One shared CSV implementation (src/utils/csv.ts) with ClientsPage, so
     * both surfaces obey the same escaping rules.
     */
    const exportRowsCsv = () => {
        const headers = ['Age days', 'Quote', 'Job', 'Client', 'Value ex GST', 'Stage'];
        downloadCsv(
            `qm-analysis-${def.id}${cell ? `-${cell.key}` : ''}-${yyyymmdd()}.csv`,
            buildCsv(headers, rows.map((r) => ({
                'Age days': r.days,
                'Quote': r.quoteNumber,
                'Job': r.address || r.title,
                'Client': r.client,
                'Value ex GST': Math.round(r.value),
                'Stage': LANE_LABEL[r.lane] || r.lane,
            }))),
        );
    };

    /**
     * Open the job. Mirrors the Run Sheet's own rule exactly (DashboardPage's
     * openProject): a job with a quote to look at opens the quote, anything
     * earlier opens the project. Diverging here would mean the same row behaved
     * differently depending on which surface you clicked it from.
     */
    const openRow = (r: DerivedRow) => {
        // Deliberately does NOT close the drawer first. Opening a job can be
        // refused: loadProject raises an unsaved-changes confirmation, and the
        // operator may cancel it. Closing up front would drop them back on the
        // board having lost their place, needing to re-click the mark and find
        // the row again. A successful open unmounts this page and takes the
        // drawer with it, so the drawer only disappears when the trip happened.
        if (r.lane === 'send' || r.lane === 'waiting' || r.lane === 'decided') {
            if (onOpenQuote) return onOpenQuote(r.raw);
        }
        if (onLoadProject) return onLoadProject(r.raw);
        return undefined;
    };

    return (
        <>
            <div className="an-ev__scrim" onClick={onClose} aria-hidden="true" />
            <div className="an-ev" ref={ref} {...modalProps}>
                <div className="an-ev__head">
                    <span className="an-ev__title" id="an-ev-title">{title}</span>
                    <button
                        type="button"
                        className="qm-inline-action-btn an-ev__close"
                        onClick={onClose}
                        aria-label="Close the evidence drawer"
                    ><Icons.X size={14} /></button>
                </div>

                <div className="an-ev__work">
                    <div className="an-ev__eq">{working}</div>
                    {mismatch && (
                        <p className="an-ev__mismatch">
                            This does not reconcile with the figure on the panel
                            ({expected.count} jobs, {money(expected.value)}). Treat the panel figure as
                            unproven until it does.
                        </p>
                    )}
                    <div className="an-ev__fields">
                        {def.fields.map((f) => (
                            <span key={f} className="an-ev__field">{f}</span>
                        ))}
                    </div>
                </div>

                <div className="an-ev__source">
                    <span className="an-ev__scope">{scopeLine}</span>
                    <span className="an-ev__sourcestamp">{def.stamp(series, ctx)}</span>
                </div>

                {/* Column heads. Without them the four figure columns are
                    unlabelled and the reader has to infer that 84px number is
                    money ex GST rather than, say, a margin. The sort is stated
                    because "highest value first" is a CHOICE, and an unstated
                    ordering invites the reader to think it is chronological. */}
                {rows.length > 0 && (
                    <div className="an-ev__cols" aria-hidden="true">
                        <span>Age</span>
                        <span>Job</span>
                        <span>Client</span>
                        <span className="an-ev__colnum">Value ex GST</span>
                        <span className="an-ev__colnum">Stage</span>
                        {/* The act column is deliberately unlabelled: its chips
                            name themselves, and a heading over "Won / Lost"
                            would only add noise. The span keeps the grid honest. */}
                        <span />
                    </div>
                )}

                <div className="an-ev__body">
                    {rows.length === 0 ? (
                        <p className="an-empty an-ev__empty">
                            No rows sit behind this figure, which means the figure should read zero.
                        </p>
                    ) : rows.map((r) => {
                        const act = acts[r.id];
                        return (
                        <div key={r.id} className="an-ev__row">
                            <button
                                type="button"
                                className="qm-inline-action-btn an-ev__rowbody"
                                onClick={() => openRow(r)}
                                title={`Open ${r.title || 'this job'} at ${r.address || 'no address recorded'}`}
                                aria-label={`Open ${r.quoteNumber || 'job'}, ${r.address || r.title || 'untitled'}, ${r.client || 'no client'}, ${money(r.value)} ex GST`}
                            >
                                <span className="an-ev__age" data-tone={ageTone(r.days)} title={`${r.days} days since this job arrived`}>{r.days}d</span>
                                <span className="an-ev__job">
                                    <span className="an-ev__quote">{r.quoteNumber || '-'}</span>
                                    <span className="an-ev__addr">{r.address || r.title || 'Untitled job'}</span>
                                </span>
                                <span className="an-ev__client">{r.client || 'No client recorded'}</span>
                                <span className="an-ev__value">{money(r.value)}</span>
                                {/* A failed write reports in the STATUS column: "the
                                    status write did not land" is status news. The lane
                                    word returns the moment the state clears. */}
                                {act === 'failed'
                                    ? <span className="an-ev__lane an-ev__lane--notsaved">Not saved</span>
                                    : <span className="an-ev__lane">{LANE_LABEL[r.lane] || r.lane}</span>}
                            </button>
                            <span className="an-ev__act">
                                {act === 'recorded' ? (
                                    <span className="an-ev__recorded">Recorded</span>
                                ) : r.lane === 'waiting' ? (
                                    <>
                                        <button
                                            type="button"
                                            className="qm-inline-action-btn an-ev__actchip an-ev__actchip--won"
                                            disabled={act === 'saving'}
                                            onClick={() => recordRow(r, 'won')}
                                            aria-label={`Mark ${r.quoteNumber || 'this quote'} won`}
                                        >Won</button>
                                        <button
                                            type="button"
                                            className="qm-inline-action-btn an-ev__actchip"
                                            disabled={act === 'saving'}
                                            onClick={() => setPendingLost(r)}
                                            aria-label={`Mark ${r.quoteNumber || 'this quote'} lost`}
                                        >Lost</button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            className="qm-inline-action-btn an-ev__actchip"
                                            onClick={() => openRow(r)}
                                            aria-label={`Open ${r.quoteNumber || 'this job'}`}
                                        >{r.lane === 'finish' ? 'Resume' : r.lane === 'send' ? 'Open quote' : 'Open'}</button>
                                        {r.lane === 'decided' && (
                                            corpusActs[r.id] === 'queued' ? (
                                                <span className="an-ev__recorded" title="Waiting in the Quote Corpus review queue (DevTools → Observe)">Queued</span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="qm-inline-action-btn an-ev__actchip"
                                                    disabled={corpusActs[r.id] === 'saving'}
                                                    onClick={() => nominateRow(r)}
                                                    title="Nominate this produced quote as a corpus exemplar. It waits in the review queue — nothing enters retrieval without approval."
                                                    aria-label={`Nominate ${r.quoteNumber || 'this quote'} to the quote corpus review queue`}
                                                >Corpus</button>
                                            )
                                        )}
                                    </>
                                )}
                            </span>
                        </div>
                        );
                    })}
                </div>

                <div className="an-ev__foot">
                    {def.caveats && def.caveats.length > 0 && (
                        <p className="an-ev__note">{def.caveats.join(' ')}</p>
                    )}
                    {handoff && (
                        <button
                            type="button"
                            className="qm-inline-action-btn an-ev__handoff"
                            onClick={() => onNavigate?.('dashboard', { pageNavOptions: handoff.options })}
                        >
                            <Icons.ArrowRight size={12} aria-hidden="true" />
                            {handoff.label}
                        </button>
                    )}
                    {rows.length > 0 && (
                        <button
                            type="button"
                            className="qm-inline-action-btn an-ev__csv"
                            onClick={exportRowsCsv}
                        >
                            <Icons.Download size={12} aria-hidden="true" />
                            Export these as CSV
                        </button>
                    )}
                    {handoffNote && <p className="an-ev__note an-ev__note--reconcile">{handoffNote}</p>}
                    <span className="an-ev__sort">Highest value first</span>
                    <span className="an-ev__count">
                        {rows.length} job{rows.length === 1 ? '' : 's'} · {money(total)} ex GST
                    </span>
                </div>
            </div>

            {/* Rendered as a SIBLING of .an-ev, never inside it: closeIfTopmost
                treats any [role="dialog"] outside the drawer's own subtree as
                "something sits on top of me", which is what keeps one Escape
                from dismissing the confirm AND the drawer together. */}
            {pendingLost && (
                <ConfirmModal
                    isOpen
                    icon="warning"
                    title="Mark this quote lost?"
                    message={`QUOTE ${pendingLost.quoteNumber || pendingLost.address || pendingLost.title || 'this job'} moves to the decided lane and counts against the win rate.\nYou can change it later.`}
                    confirmText="Mark lost"
                    cancelText="Keep waiting"
                    onConfirm={() => {
                        const r = pendingLost;
                        setPendingLost(null);
                        recordRow(r, 'lost');
                    }}
                    onClose={() => setPendingLost(null)}
                />
            )}

            <span className="sr-only" role="status" aria-live="polite">{actNotice}</span>
        </>
    );
}
